import isNil from 'lodash/isNil';
import upperFirst from 'lodash/upperFirst';
import { DateTime, IANAZone, Info } from 'luxon';

import { IVersioned } from './base';
import {
  formatDateTimeWithMilitarySupport,
  LuxonDateTimeFormats,
  toDateTime,
  Weekday
} from './chronon';
import * as ClockSpanLib from './clock-span';
import type { Maybe, Nullable } from './globals';
import * as IntervalLib from './interval';
import { millisecondsIn } from './units';
const { getTwentyFourHourClockSpan, mergeAdjacentSpans } = ClockSpanLib;

const weekdays = Info.weekdays('long', { locale: 'en' }).map(d => d.toLowerCase());

function isNonNullDate(value: unknown): value is Date {
  return value instanceof Date && !Number.isNaN(value.getTime());
}

const validScheduleKeys = ['version', ...weekdays, 'closedIntervals'];
const allowedVersions = [1];

export const DEFAULT_SCHEDULE: ISchedule = {
  version: 1,
  monday: [{ end: '17:00', start: '8:00' }],
  tuesday: [{ end: '17:00', start: '8:00' }],
  wednesday: [{ end: '17:00', start: '8:00' }],
  thursday: [{ end: '17:00', start: '8:00' }],
  friday: [{ end: '17:00', start: '8:00' }],
  closedIntervals: []
};

export interface ISchedule extends IVersioned {
  sunday?: ClockSpanLib.IClockSpan[];
  monday?: ClockSpanLib.IClockSpan[];
  tuesday?: ClockSpanLib.IClockSpan[];
  wednesday?: ClockSpanLib.IClockSpan[];
  thursday?: ClockSpanLib.IClockSpan[];
  friday?: ClockSpanLib.IClockSpan[];
  saturday?: ClockSpanLib.IClockSpan[];
  closedIntervals?: IntervalLib.ITimeInterval[];
}

/** Lowercase weekday keys stored on `ISchedule` (Luxon long names, English). */
export type IScheduleWeekdayKey = Exclude<keyof ISchedule, 'version' | 'closedIntervals'>;

function isIScheduleWeekdayKey(key: string): key is IScheduleWeekdayKey {
  return key !== 'version' && key !== 'closedIntervals' && weekdays.includes(key);
}

export function getFullyOpenSchedule(): ISchedule {
  return {
    version: 1,
    sunday: [getTwentyFourHourClockSpan()],
    monday: [getTwentyFourHourClockSpan()],
    tuesday: [getTwentyFourHourClockSpan()],
    wednesday: [getTwentyFourHourClockSpan()],
    thursday: [getTwentyFourHourClockSpan()],
    friday: [getTwentyFourHourClockSpan()],
    saturday: [getTwentyFourHourClockSpan()]
  };
}

export function getDefaultSchedule(): ISchedule {
  return { version: 1 };
}

export function getClosedScheduleTemplate(): ISchedule {
  return {
    sunday: [],
    monday: [],
    tuesday: [],
    wednesday: [],
    thursday: [],
    friday: [],
    saturday: [],
    version: 1
  };
}

export interface IHasSchedule {
  schedule?: Maybe<ISchedule>;
}

export function hasSchedule(obj: IHasSchedule): boolean {
  return Boolean(
    obj instanceof Object &&
    obj.schedule instanceof Object &&
    Object.keys(obj.schedule).length >= 2 &&
    Object.keys(obj.schedule).includes('version') &&
    Object.keys(obj.schedule)
      .filter(k => k !== 'version')
      .some(k => validScheduleKeys.includes(k))
  );
}

export const firstWeekOf2001 = IntervalLib.fromDates(
  '2001-01-01T00:00:00.000Z',
  '2001-01-08T00:00:00.000Z'
);

function toUtcDateTime(d: unknown): DateTime {
  if (d instanceof Date) {
    return DateTime.fromJSDate(d, { zone: 'utc' });
  }
  if (DateTime.isDateTime(d)) {
    return d.toUTC();
  }
  if (typeof d === 'number') {
    return DateTime.fromMillis(d, { zone: 'utc' });
  }
  if (typeof d === 'string') {
    const isoDateLike = /\d{4}-\d{2}-\d{2}/u;
    const yearPrefix = /^\d{4}/u;
    const hasDatePart = isoDateLike.test(d) || yearPrefix.test(d);
    if (hasDatePart) {
      const dt = DateTime.fromISO(d, { zone: 'utc' });
      if (dt.isValid) {
        return dt;
      }
      const dtSql = DateTime.fromSQL(d, { zone: 'utc' });
      if (dtSql.isValid) {
        return dtSql;
      }
    }
    return DateTime.invalid('invalid date string');
  }
  return DateTime.invalid('invalid input');
}

function findZuluMondayMidnight(d: unknown, offset_days: number): Nullable<Date> {
  const dt = toUtcDateTime(d);

  if (!dt.isValid) {
    return null;
  }

  return dt
    .set({ weekday: 1 + offset_days })
    .startOf('week')
    .toJSDate();
}

export function isZMM(d: unknown): boolean {
  const dt = toUtcDateTime(d);

  return dt.weekday === 1 && [dt.hour, dt.minute, dt.second].every(v => v === 0);
}

export function nextZMM(d: unknown): Nullable<Date> {
  // Ugly, but works - short-circuit if the given date
  // is a ZMM. Note that prevZMM() doesn't need this since
  // the helper function takes care of that case.
  if (isZMM(d)) {
    return toUtcDateTime(d).toJSDate();
  }

  return d ? findZuluMondayMidnight(d, 7) : null;
}

export function prevZMM(d: unknown): Nullable<Date> {
  return d ? findZuluMondayMidnight(d, 0) : null;
}

// "Widen To Zulu Monday Midnight"
// Given an input interval, return the "widened" version of it that
// starts at the previous Zulu Monday Midnight (ZMM), and ends at the next
// ZMM.
export function widenToZMM(interval: IntervalLib.ITimeInterval): IntervalLib.ITimeInterval {
  const start = prevZMM(interval.start);
  const end = nextZMM(interval.end);

  if (!isNonNullDate(start) || !isNonNullDate(end)) {
    throw new Error(
      'Core: widenToZMM(): could not resolve Zulu Monday midnight bounds for the given interval.'
    );
  }

  return IntervalLib.fromDates(start, end);
}

// Returns: 'null' if schedule is valid, otherwise a detailed error message.
export function validateSchedule(schedule: ISchedule): string | null {
  const keys = Object.keys(schedule);

  if (!keys.includes('version')) {
    return "Schedule is missing key: 'version'";
  }

  if (!allowedVersions.includes(schedule.version)) {
    return `Schedule has invalid version: '${schedule.version}'`;
  }

  for (const key of keys) {
    // check for invalid key
    if (!validScheduleKeys.includes(key)) {
      return `Schedule has an invalid key: '${key}'`;
    }

    // check for invalid clock span
    if (isIScheduleWeekdayKey(key)) {
      const clockSpanList = schedule[key];
      if (!Array.isArray(clockSpanList)) {
        return `Schedule '${key}' type is incorrect: should be array'`;
      }

      for (const clockSpan of clockSpanList) {
        const errorMessage = ClockSpanLib.validateClockSpan(clockSpan as ClockSpanLib.IClockSpan);
        if (errorMessage) {
          return `Schedule '${key}' contains an invalid clock span: ${errorMessage}`;
        }
      }
    }

    // check for invalid closed dates
    if (key === 'closedIntervals') {
      const closedIntervals = schedule.closedIntervals;
      if (!Array.isArray(closedIntervals)) {
        return `Schedule '${key}' type is incorrect: should be an array of DateTime interval`;
      }

      for (const closedInterval of closedIntervals) {
        const errorMessage = IntervalLib.validateInterval(closedInterval);
        if (errorMessage) {
          return `Schedule '${key}' contains an invalid DateTime interval: ${errorMessage}`;
        }
      }
    }
  }

  return null;
}

// Converts a "monday-based" user schedule into a monday-based interval list,
// starting at 2001-01-01 (which happens to be a monday).
export function to2001(schedule: ISchedule): IntervalLib.ITimeIntervalList {
  let result: IntervalLib.ITimeIntervalList = [];

  weekdays.forEach(day => {
    if (!isIScheduleWeekdayKey(day)) {
      return;
    }

    const daySpans = schedule[day];
    if (!daySpans) {
      return;
    }

    result = result.concat(
      daySpans.map(clockSpan => {
        const start = ClockSpanLib.clockTo2001Format(clockSpan.start, weekdays.indexOf(day) + 1);
        let end = ClockSpanLib.clockTo2001Format(clockSpan.end, weekdays.indexOf(day) + 1);

        // Round the availability to the next hour start
        // when the schedule ends at midnight, but we store 23:59
        if (end.getMinutes() === 59 && end.getUTCHours() === 23) {
          end = new Date(end.getTime() + millisecondsIn.minute);
        }

        return IntervalLib.fromDates(start, end);
      })
    );
  });

  // Remove interval gaps that are 1 minute or smaller
  return IntervalLib.mergeSmallGaps(result, millisecondsIn.minute);
}

export function adjustTzOffsetIfNegative(tzOffset_min: number): number {
  // If the timezone offset is negative, simply add
  // a week's worth of minutes to make it positive again,
  // but still equivalent (modulu arithmetic)
  const minutesInAWeek = 7 * 24 * 60;
  if (tzOffset_min < 0) {
    tzOffset_min += minutesInAWeek;
  }

  return tzOffset_min;
}

export function to2001Zulu(
  schedule: ISchedule,
  tzOffset_min: number
): IntervalLib.ITimeIntervalList {
  const hoops2001 = to2001(schedule);

  tzOffset_min = adjustTzOffsetIfNegative(tzOffset_min);

  return hoops2001.map(interval => IntervalLib.addOffset_min(interval, tzOffset_min));
}

// Same as to2001 above, but does the proper timezone "circular shift" so that it's
// in proper "Zulu Monday Midnight" format.
export function to2001ZMM(
  schedule: ISchedule,
  tzOffset_min: number
): IntervalLib.ITimeIntervalList {
  const hoops2001Zulu = to2001Zulu(schedule, tzOffset_min);

  tzOffset_min = adjustTzOffsetIfNegative(tzOffset_min);

  const shiftedWeek = IntervalLib.addOffset_min(firstWeekOf2001, tzOffset_min);

  // This is the "excess" time period past the firstWeekOf2001
  const excess = IntervalLib.fromDates(firstWeekOf2001.end, shiftedWeek.end);

  // Take whatever schedule pieces were "pushed out" into the excess
  // time period, and move them back a week, then prepend them to the output.
  let prependedIntervals = IntervalLib.intersectLists(hoops2001Zulu, [excess]);
  prependedIntervals = prependedIntervals.map(interval => IntervalLib.addOffset_week(interval, -1));

  // console.log('excess', excess);
  // console.log('prependedIntervals', prependedIntervals);

  const list = IntervalLib.intersectLists(
    [firstWeekOf2001],
    prependedIntervals.concat(hoops2001Zulu)
  );
  return IntervalLib.mergeSmallGaps(list, millisecondsIn.minute);
}

// Takes a list of intervals in 2001ZMM format, and "reifies/instantiates" by shifting
// it to line up exactly with zmmInterval.
//
// TODO: Add more documentation for this confusing function!
export function reifyToZMMInterval(
  hoops2001ZMM: IntervalLib.ITimeIntervalList,
  zmmInterval: IntervalLib.ITimeInterval
): IntervalLib.ITimeIntervalList {
  const numWeeks = IntervalLib.duration_week(zmmInterval);

  // Calculate the amount of ms that the hoops need to be shifted into the future.
  // This should work since zmmInterval should always be after the year 2001
  const shift_ms = IntervalLib.duration_ms(
    IntervalLib.fromDates(firstWeekOf2001.start, zmmInterval.start)
  );
  hoops2001ZMM = hoops2001ZMM.map(ti => IntervalLib.addOffset_ms(ti, shift_ms));

  return IntervalLib.repeatWeekly(hoops2001ZMM, numWeeks);
}

/**
 * Reifies the hours of operation like reifyToZMMInterval, then puts every endpoint on
 * the instant the warehouse clock actually shows that hour, so the hours stay the same
 * local time across a DST change.
 *
 * to2001ZMM converts the whole schedule with one offset, the one in force at the start
 * of the Zulu week (that is the tzOffset_min the caller passes). So every endpoint of
 * the reified list encodes a wall clock time: `instant + weekStartOffset = wall clock`.
 * Reading that wall clock time in the warehouse timezone again gives the right instant
 * whether DST starts or ends during the queried weeks, for any timezone and any number
 * of weeks, without having to detect the change.
 *
 * to2001ZMM cuts an interval that crosses the week boundary into two pieces. Those cut
 * endpoints are not wall clock times. reifyToZMMInterval joins the pieces back between
 * weeks, so the only cuts left are at the edges of the reified range. Reifying one extra
 * week on each side moves them a week away from zmmInterval, where reading them again can
 * only shift them by the DST difference, and the result is clipped to zmmInterval.
 *
 * toInstantInTimezone follows luxon's DateTime.fromObject, but its first guess is the
 * week-start offset. fromObject guesses with the offset in force right now, so an ambiguous
 * time (01:30 when DST ends) would change with the time of year the server runs. Here an
 * ambiguous time keeps the week-start offset when it fits, a time that does not exist (02:30
 * when DST starts) moves forward to 03:30, and most endpoints cost a single offset lookup.
 */
export function reifyToZMMIntervalWithDSTOffset(
  hoops2001ZMM: IntervalLib.ITimeIntervalList,
  zmmInterval: IntervalLib.ITimeInterval,
  timezone: string
): IntervalLib.ITimeIntervalList {
  const zone = IANAZone.create(timezone);
  const weekStartOffset_min = zone.offset(zmmInterval.start.getTime());
  const toInstantInTimezone = (date: Date): Date => {
    const offsetAtDate_min = zone.offset(date.getTime());
    if (offsetAtDate_min === weekStartOffset_min) {
      return date;
    }
    const wallClock_ms = date.getTime() + weekStartOffset_min * millisecondsIn.minute;
    const candidate_ms = wallClock_ms - offsetAtDate_min * millisecondsIn.minute;
    const offsetAtCandidate_min = zone.offset(candidate_ms);
    if (offsetAtCandidate_min === offsetAtDate_min) {
      return new Date(candidate_ms);
    }
    return new Date(
      wallClock_ms - Math.min(offsetAtDate_min, offsetAtCandidate_min) * millisecondsIn.minute
    );
  };

  const zmmIntervalWithEdgeWeeks = IntervalLib.fromDates(
    IntervalLib.addOffset_week(zmmInterval, -1).start,
    IntervalLib.addOffset_week(zmmInterval, 1).end
  );
  const reified = reifyToZMMInterval(hoops2001ZMM, zmmIntervalWithEdgeWeeks);
  const toHoursInTimezone = (ti: IntervalLib.ITimeInterval): IntervalLib.ITimeInterval => ({
    start: toInstantInTimezone(ti.start),
    end: toInstantInTimezone(ti.end)
  });
  const isBeforeZmmInterval = (ti: IntervalLib.ITimeInterval) =>
    ti.end.getTime() <= zmmInterval.start.getTime();
  const isAfterZmmInterval = (ti: IntervalLib.ITimeInterval) =>
    ti.start.getTime() >= zmmInterval.end.getTime();

  // Reading the times again keeps the intervals in order (except inside the hour skipped
  // when DST starts, see below), so walk out from zmmInterval and stop at the first
  // interval that stays outside it instead of reading every edge week.
  const firstNotBefore = reified.findIndex(ti => !isBeforeZmmInterval(ti));
  const splitIndex = firstNotBefore === -1 ? reified.length : firstNotBefore;
  const hoursOfOperation: IntervalLib.ITimeIntervalList = [];
  for (let index = splitIndex - 1; index >= 0; index--) {
    const hours = toHoursInTimezone(reified[index]);
    if (isBeforeZmmInterval(hours)) {
      break;
    }
    hoursOfOperation.unshift(hours);
  }
  for (let index = splitIndex; index < reified.length; index++) {
    const hours = toHoursInTimezone(reified[index]);
    if (isAfterZmmInterval(hours)) {
      break;
    }
    hoursOfOperation.push(hours);
  }

  // A time inside the hour skipped when DST starts moves forward an hour, past times that
  // do exist (02:30 becomes 03:30, 03:00 stays 03:00). Drop the intervals that end up
  // inverted, which only cover times that do not exist, and merge the ones that overlap.
  return IntervalLib.intersectLists(
    [zmmInterval],
    mergeIntervals(hoursOfOperation.filter(ti => ti.start.getTime() < ti.end.getTime()))
  );
}

/**
 * Methods that work on IntervalLists need the start/end dates to be Date objects, not Date strings.
 * @param intervalList
 */
export function ensureIntervalListDatesAreDateObjects(
  intervalList: IntervalLib.ITimeIntervalList
): IntervalLib.ITimeIntervalList {
  return intervalList.map(interval => ({
    start: new Date(interval.start),
    end: new Date(interval.end)
  }));
}

/*
 Check whether a particular date is under DST in a specific timezone
 */
export function isDateInDST(date: Date, timezone: string): boolean {
  return toDateTime(date, { zone: timezone }).isInDST;
}

/*
 Whether an interval straddles a DST transition in a timezone
 (exactly one endpoint is in DST). XOR, not OR: OR would be true for any
 interval fully inside summer time, which is not a DST *change*.
 */
export function isIntervalInDSTChange(start: Date, end: Date, timezone: string): boolean {
  return isDateInDST(start, timezone) !== isDateInDST(end, timezone);
}

// WARNING: unlike most other functions in this library,
// this one mutates its argument as opposed to returning a
// new object.
export function mutation_mergeDailyGaps(schedule: ISchedule): void {
  for (const weekday of Object.values(Weekday)) {
    const w = weekday.toLowerCase() as IScheduleWeekdayKey;
    const daySpans = schedule[w];
    if (daySpans) {
      schedule[w] = mergeAdjacentSpans(daySpans, millisecondsIn.minute);
    }
  }
}

export function formatScheduleClock(
  time: string,
  enableMilitaryTime: boolean,
  locale?: string
): string {
  const timeParts = time.split(':');
  const hour = parseInt(timeParts[0], 10);
  const minute = parseInt(timeParts[1], 10);
  const dateTime = DateTime.now().set({ hour, minute }).toISO();
  return formatDateTimeWithMilitarySupport(
    dateTime,
    null,
    LuxonDateTimeFormats.Extended12HrTimeAMPM,
    enableMilitaryTime,
    LuxonDateTimeFormats.Extended24HrTime,
    locale
  );
}
export function getShortDayName(day: string): string {
  const uppercaseDay = upperFirst(day);
  const daysOfWeek = [...Info.weekdays('long'), ...Info.weekdays('short')];
  if (!daysOfWeek.includes(uppercaseDay)) {
    return `'${day}' is not a valid day of the week.`;
  }
  return uppercaseDay.substring(0, 3);
}

export function intersectIntervals(
  intervalsA: ClockSpanLib.IClockSpan[],
  intervalsB: ClockSpanLib.IClockSpan[]
): ClockSpanLib.IClockSpan[] {
  const result: ClockSpanLib.IClockSpan[] = [];

  for (let i = 0; i < intervalsA.length; i++) {
    for (let j = 0; j < intervalsB.length; j++) {
      const start = Math.max(
        new Date(`1970-01-01 ${intervalsA[i].start}`).getTime(),
        new Date(`1970-01-01 ${intervalsB[j].start}`).getTime()
      );

      const end = Math.min(
        new Date(`1970-01-01 ${intervalsA[i].end}`).getTime(),
        new Date(`1970-01-01 ${intervalsB[j].end}`).getTime()
      );

      if (start < end) {
        const startAsString = new Date(start).toLocaleTimeString([], {
          hour12: false,
          hour: 'numeric',
          minute: '2-digit'
        });
        const endAsString = new Date(end).toLocaleTimeString([], {
          hour12: false,
          hour: 'numeric',
          minute: '2-digit'
        });
        result.push({
          start: startAsString,
          end: endAsString
        });
      }
    }
  }

  return result;
}

export function mergeIntervals(
  intervals: IntervalLib.ITimeInterval[]
): IntervalLib.ITimeInterval[] {
  if (intervals.length === 0) {
    return [];
  }
  intervals.sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());

  const result = [intervals[0]];
  for (let i = 1; i < intervals.length; i++) {
    const prev = result[result.length - 1];
    const current = intervals[i];

    if (new Date(prev.end).getTime() >= new Date(current.start).getTime()) {
      prev.end = new Date(Math.max(new Date(prev.end).getTime(), new Date(current.end).getTime()));
    } else {
      result.push(current);
    }
  }

  return result;
}

export function intersectSchedules(
  scheduleA: Maybe<ISchedule>,
  scheduleB: Maybe<ISchedule>
): ISchedule {
  const result: ISchedule = { version: 1, closedIntervals: [] };

  const resolvedA = isNil(scheduleA) ? { version: 1, closedIntervals: [] } : scheduleA;
  const resolvedB = isNil(scheduleB) ? { version: 1, closedIntervals: [] } : scheduleB;

  // Intersect open intervals for each day
  for (const day of Object.keys(resolvedA)) {
    if (day === 'closedIntervals' || day === 'version') {
      // eslint-disable-next-line no-continue
      continue;
    }

    if (!isIScheduleWeekdayKey(day)) {
      // eslint-disable-next-line no-continue
      continue;
    }

    const intervalsA = resolvedA[day] ?? [];
    const intervalsB = resolvedB[day] ?? [];

    result[day] = intersectIntervals(intervalsA, intervalsB);
  }

  // Merge closed intervals
  const closedIntervals = [
    ...(resolvedA.closedIntervals ?? []),
    ...(resolvedB.closedIntervals ?? [])
  ];
  result.closedIntervals = [...new Set(mergeIntervals(closedIntervals))];

  return result;
}
